import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError, take, takeUntil, timeout } from 'rxjs/operators';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { IReportCountProgress } from 'src/app/interfaces/IReportCountProgress';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { ElectionService } from 'src/app/services/election.service';

@Component({
  selector: 'lc-count-progress-table',
  templateUrl: './lc-count-progress-table.component.html',
  styleUrls: ['./lc-count-progress-table.component.scss'],
})
export class LcCountProgressTableComponent implements AfterViewInit, OnDestroy {
  @Input() district = '';

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  public title = '';
  public subTitle = '';
  public countProgress: IReportCountProgress | null = null;
  public boothWid = '10px';
  public evcWid = '10px';
  public dvWid = '10px';

  constructor(private electionService: ElectionService) {
    this.electionService.dataLoaded$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((dataLoaded) => {
        if (this.isDataLoaded(dataLoaded.electionDate)) {
          this.loadChart(this.electionService.selectedPage);
        }
      });
    this.electionService.pageChanged$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((page) => {
        this.loadChart(page);
      });
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic,
      ]);
    }
    return false;
  }

  ngAfterViewInit(): void {
    if (
      this.isDataLoaded(
        this.electionService.selectedPage.electionDateString ?? ''
      )
    ) {
      this.loadChart(this.electionService.selectedPage);
    }
  }

  private loadChart(page: ISelectPage): void {
    const election = this.electionService.electionMap.get(
      page.electionDateString ?? ''
    );
    const isFinal = election?.electionCount?.electionStatus === 'final';

    if (page.pageType === PageTypeEnum.CountLCStateWide) {
      // LC uses the same polling data as HA since they're part of the same election
      this.countProgress = {
        boothsReported: election?.electionCount?.pollingBoothsWithCounts ?? 0,
        boothsPercent: election?.electionCount?.pollingBoothCountPercent ?? 0,
        boothsTotal: election?.electionCount?.totalPollingBooths ?? 0,
        earlyVotingCentresReported:
          election?.electionCount?.earlyVotingCentresWithCounts ?? 0,
        earlyVotingCentresPercent:
          election?.electionCount?.earlyVotingCentreCountPercent ?? 0,
        earlyVotingCentresTotal:
          election?.electionCount?.totalEarlyVotingCentres ?? 0,
        declarationVotesReported:
          election?.electionCount?.declarationTypesWithCounts ?? 0,
        declarationVotesPercent:
          election?.electionCount?.declarationTypeCountPercent ?? 0,
        declarationVotesTotal:
          election?.electionCount?.totalDeclarationTypes ?? 0,
        isFinal: isFinal,
      };
    }
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
